/**
 * Trades - list (Active / Pending Review / History), propose, accept/reject/veto.
 */
import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import { Plus, X, Check, ArrowLeftRight, Clock } from "lucide-react";
import { useTheme } from "../contexts/ThemeContext";
import { useSeason } from "../contexts/SeasonContext";
import { useAuth, authHeaders } from "../contexts/AuthContext";
import SeasonShell from "../components/season/SeasonShell";
import CpuBadge from "../components/common/CpuBadge";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function Trades() {
  const { id: leagueId } = useParams();
  const { isDark } = useTheme();
  const { actingAsTeamId } = useSeason();
  const { user } = useAuth();
  const [tab, setTab] = useState("PROPOSED");
  const [trades, setTrades] = useState([]);
  const [season, setSeason] = useState(null);
  const [error, setError] = useState(null);
  const [showPropose, setShowPropose] = useState(false);
  // [TD-89 v2 Verdict T.A] Inline alert (NOT toast) when the backend
  // auto-declines because the receiving team is CPU-controlled. The
  // alert lives until the user dismisses it with "Got it".
  const [cpuDeclineAlert, setCpuDeclineAlert] = useState(null);

  const myTeam = season?.teams?.find((t) => t.id === actingAsTeamId)
              || season?.teams?.find((t) => t.user_id === user?.id);

  const refresh = async () => {
    try {
      const [s, t] = await Promise.all([
        axios.get(`${API}/season/${leagueId}`, { headers: authHeaders() }),
        axios.get(`${API}/trades/${leagueId}${tab && tab !== "ALL" ? `?status=${tab}` : ""}`, { headers: authHeaders() }),
      ]);
      setSeason(s.data);
      setTrades(t.data || []);
    } catch (e) { setError(e?.response?.data?.detail || e.message); }
  };

  useEffect(() => { refresh(); }, [leagueId, tab]);

  const action = async (id, path) => {
    setError(null);
    try {
      await axios.post(`${API}/trades/${id}/${path}`, {}, { headers: authHeaders() });
      await refresh();
    } catch (e) { setError(e?.response?.data?.detail || e.message); }
  };

  const muted = isDark ? "text-[#A0A0A0]" : "text-[#404040]";
  const fg = isDark ? "text-[#F5F5F5]" : "text-[#111111]";
  const cardBg = isDark ? "bg-[#1C1C1C]" : "bg-white";
  const cardBorder = isDark ? "border-[#2A2A2A]" : "border-[#D0D0D0]";

  return (
    <SeasonShell>
      <div className="flex items-center justify-between flex-wrap gap-3 mb-4">
        <div>
          <div className="text-[10px] uppercase tracking-[0.3em] text-[#FF6B00] font-display font-bold mb-1">Trades</div>
          <h1 className="font-display font-black text-3xl sm:text-4xl uppercase tracking-tight">Trade Block</h1>
        </div>
        <button onClick={() => setShowPropose(true)} className="flex items-center gap-1.5 px-4 py-2 rounded-lg bg-[#FF6B00] hover:bg-[#FF8533] text-white text-sm font-display font-bold uppercase tracking-wide" data-testid="trades-propose">
          <Plus size={14} /> Propose Trade
        </button>
      </div>

      {error && <div className="mb-3 px-4 py-2 rounded-lg bg-red-500/10 border border-red-500/40 text-red-400 text-sm" data-testid="trades-error">{error}</div>}

      {/* [TD-89 v2 Verdict T.A] CPU auto-decline inline alert (NOT toast).
          Persists until user clicks "Got it". */}
      {cpuDeclineAlert && (
        <div
          data-testid="cpu-auto-decline-alert"
          className="mb-3 px-4 py-3 rounded-lg bg-[#FF6B00]/10 border border-[#FF6B00]/40 text-[#FF6B00] text-sm flex items-start justify-between gap-3"
        >
          <div className="flex items-start gap-2 min-w-0">
            <CpuBadge isDark={isDark} size="md" testId="cpu-auto-decline-badge" />
            <span data-testid="cpu-auto-decline-message" className="font-body">{cpuDeclineAlert}</span>
          </div>
          <button
            data-testid="cpu-auto-decline-dismiss"
            onClick={() => setCpuDeclineAlert(null)}
            className="px-3 py-1 rounded-md bg-[#FF6B00]/20 hover:bg-[#FF6B00]/30 font-display font-bold text-xs uppercase tracking-wider whitespace-nowrap"
          >
            Got it
          </button>
        </div>
      )}

      <div className="flex gap-1 mb-4 flex-wrap">
        {["PROPOSED", "REVIEW", "COMPLETED", "REJECTED", "VETOED", "CANCELLED"].map((s) => (
          <button
            key={s}
            onClick={() => setTab(s)}
            className={`px-3 py-1.5 rounded-lg text-xs font-display font-bold uppercase tracking-wide transition ${
              tab === s ? "bg-[#FF6B00]/15 text-[#FF6B00]" : `${muted} hover:${fg} border ${cardBorder}`
            }`}
            data-testid={`trades-tab-${s}`}
          >
            {s}
          </button>
        ))}
      </div>

      <div className="space-y-3" data-testid="trades-list">
        {trades.length === 0 && (
          <div className={`rounded-2xl ${cardBg} border ${cardBorder} p-8 text-center ${muted} text-sm`}>
            No {tab.toLowerCase()} trades.
          </div>
        )}
        {trades.map((tr) => (
          <TradeCard key={tr.id} trade={tr} myTeam={myTeam} isDark={isDark} onAction={action} />
        ))}
      </div>

      {showPropose && myTeam && season && (
        <ProposeTradeModal
          isDark={isDark}
          leagueId={leagueId}
          myTeam={myTeam}
          teams={season.teams.filter((t) => t.id !== myTeam.id)}
          onClose={() => setShowPropose(false)}
          onSuccess={(result) => {
            setShowPropose(false);
            // [TD-89 v2 Verdict T.A] If the backend auto-declined because
            // the receiving team is CPU, surface the locked decline message
            // as a persistent inline alert (NOT a toast).
            if (result?.cpu_auto_declined) {
              setCpuDeclineAlert(result.decline_message || "Trade declined. CPU teams do not accept trade proposals.");
              setTab("REJECTED");
            } else {
              setTab("PROPOSED");
            }
            refresh();
          }}
        />
      )}
    </SeasonShell>
  );
}


function TradeCard({ trade, myTeam, isDark, onAction }) {
  const muted = isDark ? "text-[#A0A0A0]" : "text-[#404040]";
  const cardBg = isDark ? "bg-[#1C1C1C]" : "bg-white";
  const cardBorder = isDark ? "border-[#2A2A2A]" : "border-[#D0D0D0]";

  const isReceiving = myTeam?.id === trade.receiving_team_id;
  const isProposing = myTeam?.id === trade.proposing_team_id;
  const canVeto = trade.status === "REVIEW" && !isReceiving && !isProposing;
  // [TD-89 v2 Verdict T.A] Distinct trade-history label when the trade
  // was auto-declined because the receiving team is CPU-controlled.
  const isCpuAutoDeclined = trade.decline_reason === "auto_declined_cpu";

  return (
    <div className={`rounded-2xl ${cardBg} border ${cardBorder} p-4`} data-testid={`trade-card-${trade.id}`}>
      <div className="flex items-center justify-between mb-3">
        <div className="text-xs uppercase tracking-widest font-display font-bold text-[#FF6B00] flex items-center gap-2">
          <ArrowLeftRight size={14} />
          {trade.proposing_team_name} → {trade.receiving_team_name}
        </div>
        <div className="flex items-center gap-2">
          {isCpuAutoDeclined && (
            <span
              data-testid={`trade-cpu-auto-declined-label-${trade.id}`}
              title="This trade was automatically declined because the receiving team is CPU-controlled."
              className="px-2 py-1 rounded text-[10px] uppercase tracking-widest font-display font-bold bg-[#FF6B00]/15 text-[#FF6B00] border border-[#FF6B00]/30"
            >
              CPU auto-declined
            </span>
          )}
          <StatusPill status={trade.status} reviewEndsAt={trade.review_ends_at} isDark={isDark} />
        </div>
      </div>

      <div className="grid grid-cols-2 gap-3 mb-3">
        <div className={`p-2 rounded-lg ${isDark ? "bg-[#252525]" : "bg-[#F5F5F5]"}`}>
          <div className={`text-[10px] uppercase tracking-widest ${muted} mb-1`}>{trade.proposing_team_name} sends</div>
          {(trade.players_offered || []).filter(Boolean).map((p) => (
            <div key={p.id} className="text-xs"><span className="font-bold">{p.name}</span> · {p.position}</div>
          ))}
          {trade.faab_offered > 0 && <div className="text-xs text-[#00C853]">+ ${trade.faab_offered} FAAB</div>}
        </div>
        <div className={`p-2 rounded-lg ${isDark ? "bg-[#252525]" : "bg-[#F5F5F5]"}`}>
          <div className={`text-[10px] uppercase tracking-widest ${muted} mb-1`}>{trade.receiving_team_name} sends</div>
          {(trade.players_requested || []).filter(Boolean).map((p) => (
            <div key={p.id} className="text-xs"><span className="font-bold">{p.name}</span> · {p.position}</div>
          ))}
          {trade.faab_requested > 0 && <div className="text-xs text-[#00C853]">+ ${trade.faab_requested} FAAB</div>}
        </div>
      </div>

      <div className="flex flex-wrap gap-2">
        {isReceiving && trade.status === "PROPOSED" && (
          <>
            <button onClick={() => onAction(trade.id, "accept")} className="flex items-center gap-1 px-3 py-1.5 rounded-lg bg-[#00C853]/15 border border-[#00C853]/40 text-[#00C853] hover:bg-[#00C853]/25 text-xs font-display font-bold uppercase" data-testid={`trade-accept-${trade.id}`}>
              <Check size={12} /> Accept
            </button>
            <button onClick={() => onAction(trade.id, "reject")} className="flex items-center gap-1 px-3 py-1.5 rounded-lg bg-red-500/10 border border-red-500/30 text-red-400 hover:bg-red-500/20 text-xs font-display font-bold uppercase" data-testid={`trade-reject-${trade.id}`}>
              <X size={12} /> Reject
            </button>
          </>
        )}
        {isProposing && trade.status === "PROPOSED" && (
          <button onClick={() => onAction(trade.id, "cancel")} className="flex items-center gap-1 px-3 py-1.5 rounded-lg bg-gray-500/10 border border-gray-500/30 text-gray-400 hover:bg-gray-500/20 text-xs font-display font-bold uppercase" data-testid={`trade-cancel-${trade.id}`}>
            <X size={12} /> Cancel
          </button>
        )}
        {canVeto && (
          <button onClick={() => onAction(trade.id, "veto")} className="flex items-center gap-1 px-3 py-1.5 rounded-lg bg-yellow-500/10 border border-yellow-500/40 text-yellow-400 hover:bg-yellow-500/20 text-xs font-display font-bold uppercase" data-testid={`trade-veto-${trade.id}`}>
            Veto ({(trade.veto_votes || []).length})
          </button>
        )}
      </div>
    </div>
  );
}


function StatusPill({ status, reviewEndsAt, isDark }) {
  const map = {
    PROPOSED: "bg-yellow-500/15 text-yellow-400",
    REVIEW: "bg-blue-500/15 text-blue-400",
    COMPLETED: "bg-[#00C853]/15 text-[#00C853]",
    REJECTED: "bg-red-500/15 text-red-400",
    VETOED: "bg-red-500/15 text-red-400",
    CANCELLED: "bg-gray-500/15 text-gray-400",
    EXPIRED: "bg-gray-500/15 text-gray-400",
  };
  const cls = map[status] || "";
  let label = status;
  if (status === "REVIEW" && reviewEndsAt) {
    const ms = new Date(reviewEndsAt) - new Date();
    if (ms > 0) {
      const hours = Math.floor(ms / 3600000);
      label = `REVIEW · ${hours}h left`;
    }
  }
  return <span className={`px-2 py-1 rounded text-[10px] uppercase tracking-widest font-display font-bold ${cls}`}>{label}</span>;
}


function ProposeTradeModal({ isDark, leagueId, myTeam, teams, onClose, onSuccess }) {
  const [partnerId, setPartnerId] = useState(teams[0]?.id || "");
  const [myRoster, setMyRoster] = useState([]);
  const [theirRoster, setTheirRoster] = useState([]);
  const [give, setGive] = useState([]);
  const [get, setGet] = useState([]);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    axios.get(`${API}/teams/${myTeam.id}/roster`, { headers: authHeaders() }).then((r) => setMyRoster(r.data || []));
  }, [myTeam.id]);

  useEffect(() => {
    if (!partnerId) return;
    axios.get(`${API}/teams/${partnerId}/roster`, { headers: authHeaders() }).then((r) => setTheirRoster(r.data || []));
    setGet([]);
  }, [partnerId]);

  const toggle = (arr, set, id) => {
    if (arr.includes(id)) set(arr.filter((x) => x !== id));
    else set([...arr, id]);
  };

  const submit = async () => {
    setBusy(true); setError(null);
    try {
      const { data } = await axios.post(`${API}/trades/${leagueId}`, {
        proposing_team_id: myTeam.id,
        receiving_team_id: partnerId,
        players_offered: give,
        players_requested: get,
        faab_offered: 0,
        faab_requested: 0,
      }, { headers: authHeaders() });
      onSuccess(data);
    } catch (e) { setError(e?.response?.data?.detail || e.message); }
    finally { setBusy(false); }
  };

  const cardBg = isDark ? "bg-[#181818]" : "bg-white";
  const cardBorder = isDark ? "border-[#2A2A2A]" : "border-[#D0D0D0]";
  const muted = isDark ? "text-[#A0A0A0]" : "text-[#404040]";

  return (
    <div className="fixed inset-0 z-50 bg-black/70 backdrop-blur-sm flex items-center justify-center p-4" onClick={onClose}>
      <div className={`rounded-2xl ${cardBg} border ${cardBorder} max-w-3xl w-full max-h-[85vh] overflow-hidden flex flex-col`} onClick={(e) => e.stopPropagation()}>
        <div className="p-4 border-b border-[#2A2A2A] flex items-center justify-between">
          <span className="text-[10px] uppercase tracking-widest text-[#FF6B00] font-display font-bold">Propose Trade</span>
          <button onClick={onClose} className={muted} data-testid="propose-close"><X size={16} /></button>
        </div>

        <div className="p-4 flex-1 overflow-y-auto">
          <div className="mb-3">
            <div className={`text-[10px] uppercase tracking-widest ${muted} mb-1`}>Trade with</div>
            {/* [TD-89 v2 Verdict T.A] Button-list partner selector - renders
                `<CpuBadge />` next to CPU-controlled teams so the user sees
                the auto-decline path BEFORE submitting. Option is NOT
                disabled (founder direction - discovery moment). */}
            <div
              data-testid="propose-partner-list"
              className={`grid grid-cols-1 sm:grid-cols-2 gap-1 max-h-44 overflow-y-auto rounded-lg border ${cardBorder} ${isDark ? "bg-[#252525]/40" : "bg-[#F0F0F0]"} p-1`}
            >
              {teams.map((t) => {
                const isCpu = !!t.is_cpu;
                const selected = partnerId === t.id;
                return (
                  <button
                    key={t.id}
                    onClick={() => setPartnerId(t.id)}
                    data-testid={`propose-partner-${t.id}`}
                    className={`flex items-center justify-between gap-2 px-3 py-2 rounded-md text-left text-sm transition ${
                      selected
                        ? (isDark ? "bg-[#FF6B00]/20 ring-1 ring-[#FF6B00] text-[#F5F5F5]" : "bg-[#FF6B00]/15 ring-1 ring-[#FF6B00] text-[#0A0A0A]")
                        : (isDark ? "text-[#F5F5F5] hover:bg-white/[0.06]" : "text-[#0A0A0A] hover:bg-black/[0.05]")
                    }`}
                  >
                    <span className="font-body truncate">{t.team_name}</span>
                    {isCpu && <CpuBadge isDark={isDark} size="sm" testId={`propose-partner-${t.id}-cpu-badge`} />}
                  </button>
                );
              })}
            </div>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <RosterPicker title="You give" roster={myRoster} selected={give} onToggle={(id) => toggle(give, setGive, id)} muted={muted} testid="propose-give" />
            <RosterPicker title="You get" roster={theirRoster} selected={get} onToggle={(id) => toggle(get, setGet, id)} muted={muted} testid="propose-get" />
          </div>

          {error && <div className="mt-3 px-3 py-2 rounded-lg bg-red-500/10 border border-red-500/40 text-red-400 text-xs">{error}</div>}
        </div>

        <div className="p-4 border-t border-[#2A2A2A]">
          <button
            onClick={submit} disabled={!partnerId || busy || (give.length === 0 && get.length === 0)}
            className="w-full px-4 py-2.5 rounded-lg bg-[#FF6B00] hover:bg-[#FF8533] text-white font-display font-bold uppercase tracking-wide text-sm disabled:opacity-50"
            data-testid="propose-submit"
          >
            {busy ? "Sending…" : "Send Offer"}
          </button>
        </div>
      </div>
    </div>
  );
}


function RosterPicker({ title, roster, selected, onToggle, muted, testid }) {
  return (
    <div>
      <div className={`text-[10px] uppercase tracking-widest ${muted} mb-1`}>{title}</div>
      <div className="space-y-1 max-h-72 overflow-y-auto">
        {roster.map((r) => (
          <button
            key={r.player.id}
            onClick={() => onToggle(r.player.id)}
            className={`w-full text-left px-2 py-1.5 rounded-lg text-xs transition ${
              selected.includes(r.player.id) ? "bg-[#FF6B00]/15 ring-1 ring-[#FF6B00]" : "hover:bg-[#FF6B00]/5"
            }`}
            data-testid={`${testid}-${r.player.id}`}
          >
            <span className="font-display font-bold uppercase">{r.player.name}</span> · {r.player.position} · Proj {Math.round(r.player.projected_points || 0)}
          </button>
        ))}
      </div>
    </div>
  );
}
